import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LoginWrapper } from "./styles";
import { Row, Col, Div, Text } from "../../styles/Common";

import back from "../../assets/icons/arrow-left.svg";
import { Card } from "../../components/Card";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Password } from "../../components/Password";
import { theme } from "../../styles/theme";
import { validateLoginForm } from "../../utilities/validations";
import logo from "../../assets/images/logo.png";
import useStartAuth from "./hooks/useStartAuth";
import useVerifyOTP from "./hooks/useVerifyOTP";
import { Alert } from "antd";
import { setLocalUser } from "../../core/services/axiosInstance";
import { Link } from "../../components/Link";
import useGetPermissions from "../../core/hooks/useGetPermissions";
import { getUserPermissionsLogin, setPermissions } from "../../utilities/permissions";
import { useMediaQuery } from "react-responsive";

export const Login = () => {
  const isMobile = useMediaQuery({
    query: `(max-width: ${theme.breakpoints.sm})`,
  });
  const navigate = useNavigate();
  const getPermissions = useGetPermissions();
  const authentication = useStartAuth();
  const verifyOTP = useVerifyOTP();
  const { isLoading } = authentication;

  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(300);
  const [resendTimer, setResendTimer] = useState(60);
  const [resendCount, setResendCount] = useState(0);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [infoMessage, setInfoMessage] = useState(null);

  const [forceUpdate, setForceUpdate] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [errorForm, setErrorForm] = useState({
    email: { error: false, message: "" },
    password: { error: false, message: "" },
  });
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let interval;
    let timeout;
    let resendInterval;
    
    if (step === 2 && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (step === 2 && timer === 0) {
      setErrorMessage("OTP expired. Please log in again.");
      timeout = setTimeout(() => {
        setOtp("");
        setErrorMessage("");
        setStep(1);
        setTimer(300);
        setResendTimer(60);
        setResendCount(0);
        setIsResendDisabled(true);
      }, 4000);
    }

    if (step === 2 && resendTimer > 0 && isResendDisabled) {
      resendInterval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (step === 2 && resendTimer === 0 && isResendDisabled) {
      setIsResendDisabled(false);
    }
    
    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
      if (resendInterval) clearInterval(resendInterval);
    };
  }, [step, timer, resendTimer, isResendDisabled]);

  const handleChange = (event) => {
    const { value, id } = event.target;

    const newErrorForm = { ...errorForm, [id]: { error: false, message: "" } };
    const newLoginForm = { ...loginForm, [id]: value };
    setLoginForm(newLoginForm);
    setErrorForm(newErrorForm);
    setForceUpdate(!forceUpdate);
  };

  const handleLogin = () => {
    setErrorMessage("");
    
    const validation = validateLoginForm.validate(loginForm, {
      abortEarly: false,
    });

    if (validation.error) {
      const newErrorForm = { ...errorForm };
      validation.error.details.forEach((ele) => {
        newErrorForm[ele.context.label].error = true;
        newErrorForm[ele.context.label].message =
          "Required field or invalid format";
      });
      setErrorForm(newErrorForm);
      setForceUpdate(!forceUpdate);
    } else {
      authentication.reset();
      authentication.mutate(loginForm, {
        onSuccess: (res) => {
          setInfoMessage(res?.data?.InfoMessage);
          setErrorMessage("");
          setStep(2);
          setTimer(300);
          setResendTimer(60);
          setResendCount(0);
          setIsResendDisabled(true);
        },
        onError: (err) => {
          setErrorMessage(err.response?.data?.Errors?.[0] || "Login error");
        },
      });
    }
  };

  const handleOtpChange = (event) => {
    setOtp(event.target.value);
  };

  const handleVerifyOtp = () => {
    setErrorMessage("");
    
    verifyOTP.reset();
    verifyOTP.mutate(
      { email: loginForm.email, code: otp },
      {
        onSuccess: (res) => {
          setLocalUser(res.data.token);
          getPermissions.reset();
          getPermissions.mutate(null, {
            onSuccess: (response) => {
              setPermissions(response?.data);
              const hasWeldingInspector = Object.values(getUserPermissionsLogin(JSON.stringify(response?.data))).some(role => role.role === "Welding Inspector");
              if (isMobile && hasWeldingInspector) {
                navigate("/application");
              } else {
                navigate("/projects");
              }
            },
          });
          setErrorMessage("");
        },
        onError: (err) => {
          setErrorMessage(
            err?.response?.data || "Verification error"
          );
        },
      }
    );
  };

  const handleReset = () => {
    navigate("/reset-password-request");
  };

  const handleResendOTP = () => {
    setErrorMessage("");
    authentication.reset();
    authentication.mutate(loginForm, {
      onSuccess: (res) => {
        setErrorMessage("");
        setResendCount(prev => prev + 1);
        setResendTimer(60);
        setIsResendDisabled(true);
        setTimer(300);
      },
      onError: (err) => {
        const errorMsg = err?.response?.data?.Errors?.[0] || err?.response?.data || "Resend error";
        if (errorMsg.includes("limit") || errorMsg.includes("hour")) {
          setErrorMessage("Maximum resend attempts reached for this hour. Please try again later.");
          setTimeout(() => {
            setOtp("");
            setErrorMessage("");
            setStep(1);
            setTimer(300);
            setResendTimer(60);
            setResendCount(0);
            setIsResendDisabled(true);
          }, 4000);
        } else {
          setErrorMessage(errorMsg);
        }
      },
    });
  };

  const handleBack = () => {
    setOtp("");
    setErrorMessage("");
    setTimer(300);
    setResendTimer(60);
    setResendCount(0);
    setIsResendDisabled(true);
    setStep(1);
  };

  const formatTimer = () => {
    const minutes = Math.floor(timer / 60)
      .toString()
      .padStart(2, "0");
    const seconds = (timer % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const formatResendTimer = () => {
    return `${resendTimer}s`;
  };

  return (
    <LoginWrapper>
      <Row width="1200px" height="700px">
        {!isMobile && (
          <Col width="50%" height="100%">
            <img alt="logo" src={logo} width="336px" height="143px" />
          </Col>
        )}
        <Col width={isMobile ? "100%" : "50%"} height="100%" align="center" justify="center">
          <Card width="380px">
            <Div direction="column">
              <Div direction="column" align="center" gap="5px" m="0 0 32px 0">
                {isMobile && (
                  <img 
                    alt="logo" 
                    src={logo} 
                    width="161px" 
                    height="76px" 
                    style={{ marginBottom: "28px" }}
                  />
                )}
                {infoMessage !== null && (
                  <Div m="10px 0 10px 0" justify="flex-end">
                    <Alert 
                      message={infoMessage} 
                      type="error"
                    />
                  </Div>
                )}
                <Text
                  color={theme.colors.dark2}
                  weight={theme.fonts.weight.bold}
                  size={theme.fonts.size.h3}
                >
                  Welcome
                </Text>
                <Text
                  color={theme.colors.grey}
                  weight={theme.fonts.weight.regular}
                  size={theme.fonts.size.default}
                >
                  {step === 1
                    ? "Login to manage your projects"
                    : "An OTP was sent to your email. You have 5 minutes to enter it."}
                </Text>
              </Div>

              {step === 1 ? (
                <>
                  <Div direction="column" gap="16px" width="287px">
                    <Input
                      placeholder="Username"
                      id="email"
                      onChange={handleChange}
                      error={errorForm.email.error}
                      helper={
                        errorForm.email.error ? errorForm.email.message : ""
                      }
                    />
                    <Password
                      placeholder="Password"
                      id="password"
                      onChange={handleChange}
                      error={errorForm.password.error}
                      helper={
                        errorForm.password.error
                          ? errorForm.password.message
                          : ""
                      }
                      onPressEnter={handleLogin}
                    />
                  </Div>
                  <Div justify="flex-end">
                    <Link
                      size={theme.fonts.size.sm}
                      style={{ marginBottom: "35px" }}
                      onClick={handleReset}
                    >
                      Reset Password
                    </Link>
                  </Div>
                </>
              ) : (
                <>
                  <Input
                    placeholder="Enter OTP"
                    id="otp"
                    value={otp}
                    onChange={handleOtpChange}
                  />
                  <Div justify="center" m={"5px 0 0 0"}>
                    <Text
                      size={theme.fonts.size.default}
                      color={theme.colors.salmon}
                      align="center"
                      m="10px 0"
                    >
                      {formatTimer()}
                    </Text>
                  </Div>
                  <Div direction="column" align="center" justify="center" m={"10px 0"}>
                    <Link
                      onClick={handleResendOTP}
                      disabled={isResendDisabled || resendCount >= 3}
                      loading={authentication.isLoading}
                    >
                      {isResendDisabled && resendCount < 3 
                        ? `Resend OTP (${formatResendTimer()})` 
                        : resendCount >= 3 
                          ? "Max attempts reached" 
                          : "Resend OTP"
                      }
                    </Link>
                    {resendCount >= 3 && (
                      <Text size={theme.fonts.size.default} color={theme.colors.salmon} align="center" m="10px 0">
                        Maximum resend attempts reached. Please log in again.
                      </Text>
                    )}
                  </Div>
                </>
              )}

              {errorMessage && (
                <Div m="10px 0 10px 0" justify="flex-end">
                  <Alert 
                    closable 
                    message={`${errorMessage}`} 
                    type="error"
                    onClose={() => setErrorMessage("")}
                  />
                </Div>
              )}

              <Div>
                {step === 1 ? (
                  <Button
                    onClick={handleLogin}
                    width="100%"
                    background={theme.colors.salmon}
                    loading={isLoading}
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    onClick={handleVerifyOtp}
                    width="100%"
                    style={{ marginTop: "15px" }}
                    background={theme.colors.salmon}
                    disabled={timer === 0}
                  >
                    Verify and Login
                  </Button>
                )}
              </Div>
              {step === 2 && (
                <Div justify="flex-start" m={"10px 0 0 0"}>
                  <Link
                    size={theme.fonts.size.sm}
                    style={{ marginBottom: "35px" }}
                    color={theme.colors.grey1}
                    onClick={handleBack}
                    icon={
                      <img
                        alt="back"
                        src={back}
                        style={{
                          marginRight: "5px",
                        }}
                      />
                    }
                  >
                    Back to login form
                  </Link>
                </Div>
              )}
            </Div>
          </Card>
        </Col>
      </Row>
    </LoginWrapper>
  );
};
